import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Icon } from '@/components/Icon';
import { fetchTopicMeetings } from '@/lib/api';
import { isContested, STAT_META, type StatKey } from '@/lib/topics';
import './TopicDrillDown.css';

type SortKey = 'date' | 'city' | 'vote';

/**
 * The meetings behind a stat card or a leaderboard row.
 *
 * This exists because of a specific fix: drill-downs must show the real per-topic content —
 * city, date, item type, number, vote and an excerpt — not one hardcoded dataset reused across
 * every topic filter. So every row here is fetched for this topic AND this filter.
 *
 * Sortable, unlike the Legislative History list. Different surface, different decision: there
 * the single sort keeps a small list simple, here an analyst is comparing dozens of rows.
 */
export function TopicDrillDown({
  topicId,
  topicName,
  filter,
  onClose,
}: {
  topicId: string;
  topicName: string;
  filter: { stat?: StatKey; city?: string };
  onClose: () => void;
}) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const [sort, setSort] = useState<SortKey>('date');

  const { data, isLoading } = useQuery({
    queryKey: ['topic-meetings', topicId, filter.stat, filter.city],
    queryFn: () => fetchTopicMeetings(topicId, filter),
  });

  useEffect(() => {
    closeRef.current?.focus();
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const rows = [...(data ?? [])].sort((a, b) => {
    if (sort === 'city') return a.city.localeCompare(b.city);
    if (sort === 'vote') return (b.vote ?? '').localeCompare(a.vote ?? '');
    return a.date < b.date ? 1 : -1;
  });

  const scopeLabel = filter.city
    ? filter.city
    : filter.stat
      ? STAT_META[filter.stat].label
      : 'All meetings';

  return (
    <div className="drill" role="dialog" aria-modal="true" aria-label={`${topicName} — ${scopeLabel}`}>
      <header className="drill__head">
        <div>
          <p className="u-meta">{topicName}</p>
          <h2 className="drill__title">{scopeLabel}</h2>
          <p className="drill__count">
            {rows.length} {rows.length === 1 ? 'meeting' : 'meetings'}
          </p>
        </div>
        <button ref={closeRef} type="button" className="drill__close" onClick={onClose}>
          Close
        </button>
      </header>

      <div className="drill__sorts">
        {(['date', 'city', 'vote'] as SortKey[]).map((key) => (
          <button
            key={key}
            type="button"
            className="drill__sort"
            data-active={sort === key}
            onClick={() => setSort(key)}
          >
            {key === 'date' ? 'Newest' : key === 'city' ? 'City' : 'Vote'}
          </button>
        ))}
      </div>

      <div className="drill__rows">
        {isLoading && <p className="drill__empty">Loading…</p>}
        {!isLoading && rows.length === 0 && (
          <p className="drill__empty">Nothing matches this filter.</p>
        )}
        {rows.map((row) => (
          <article key={row.meetingId} className="drill__row">
            <div className="drill__rowHead">
              <strong className="drill__city">
                {row.city}, {row.region}
              </strong>
              <span className="drill__date">{row.date}</span>
            </div>
            <p className="drill__excerpt">…{row.excerpt}…</p>
            <div className="drill__meta">
              {row.itemType && <span className="drill__type">{row.itemType}</span>}
              {row.num && <span className="drill__num">{row.num}</span>}
              {/* Vote read straight from the field. Null is stated, not omitted. */}
              {row.vote ? (
                <span className="drill__vote" data-contested={isContested(row.vote)}>
                  <Icon name="check" /> {row.vote}
                </span>
              ) : (
                <span className="drill__vote" data-none="true">
                  No vote taken
                </span>
              )}
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}
